import environment from '@lib/environment';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import { DiagnosticsModule } from '@modules/DiagnosticsModule';
import FullStoryModule from '@modules/FullStoryModule';

export class SentryModule extends AppModule {
  override preload(): void {
    this.app.events.on('app.started', () => {
      if (environment.SENTRY_DSN) {
        Sentry.init({
          dsn: environment.SENTRY_DSN,
          environment: environment.RUNTIME_ENV,
          integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
              useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
            DiagnosticsModule.sentryIntegration(),
          ],
          release: environment.APP_VERSION,
          tracesSampleRate: 0.1,
          attachStacktrace: true,
          sendDefaultPii: true,
        });
        this.app.logger.info('initialized');
      }
    });

    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      if (environment.SENTRY_DSN) {
        Sentry.setUser({
          id: user.id,
          username: user.name,
          ip_address: '{{auto}}',
        });
        Sentry.setContext('Org', {
          id: user.org.id,
          name: user.org.name,
          contactCenterId: user.contactCenterId,
          contactCenterUserId: user.contactCenterUserId,
          hasActiveContract: user.org.hasActiveContract,
          accountId: user.accountId,
        });
        Sentry.setContext('FullStory', {
          sessionUrl: FullStoryModule.sessionUrl,
        });
        this.app.logger.info('user identified: ', user.id);
      }
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      if (environment.SENTRY_DSN) {
        Sentry.setUser(null);
      }
    });
  }
}

export default new SentryModule(appManager.events, appManager.logger('Sentry'));
